import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, ExternalLink, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl, formatDate } from "@/lib/api";
import { TranslationsPanel } from "@/components/admin/TranslationsPanel";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const Status = ({ s }) => (
  <span className={`rounded-full px-2.5 py-1 font-mono text-[10.5px] uppercase tracking-wider ${s === "published" ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`}>{s}</span>
);

export default function AdminPosts() {
  const [posts, setPosts] = useState(null);
  const [del, setDel] = useState(null);

  const load = () => api.get("/admin/posts").then((r) => setPosts(Array.isArray(r.data) ? r.data : [])).catch((e) => { toast.error(errMsg(e)); setPosts([]); });
  useEffect(() => { load(); }, []);

  const remove = async () => {
    try { await api.delete(`/admin/posts/${del.id}`); toast.success("Post deleted"); setDel(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-posts-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-violet-700">Insights</p>
          <h1 className="mt-2 font-display text-4xl font-medium text-slate-900">Blog posts</h1>
        </div>
        <Link to="/admin/posts/new" data-testid="admin-new-post-button" className="inline-flex h-11 items-center gap-2 rounded-full bg-[#0B0716] px-5 text-sm font-semibold text-white transition-colors hover:bg-violet-700"><Plus className="h-4 w-4" /> New post</Link>
      </div>
      <div className="mt-8 overflow-hidden rounded-3xl border border-slate-200 bg-white">
        {posts === null && <p className="p-8 text-slate-500">Loading…</p>}
        {posts?.length === 0 && (
          <div className="flex flex-col items-center gap-3 p-14 text-center" data-testid="admin-posts-empty">
            <FileText className="h-8 w-8 text-violet-600" />
            <p className="font-display text-xl text-slate-900">No posts yet</p>
            <p className="text-sm text-slate-500">Write your first Insights article.</p>
          </div>
        )}
        {posts?.map((p) => (
          <div key={p.id} data-testid={`admin-post-row-${p.id}`} className="flex items-center gap-4 border-b border-slate-100 p-4 last:border-0 sm:p-5">
            <div className="hidden h-14 w-20 shrink-0 overflow-hidden rounded-xl bg-[#F3F2F8] sm:block">
              {p.cover_image && <img src={fileUrl(p.cover_image)} alt="" className="h-full w-full object-cover" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate font-display text-lg text-slate-900">{p.title}</p>
              <p className="mt-1 flex flex-wrap items-center gap-2 text-[12.5px] text-slate-500"><Status s={p.status} /> Updated {formatDate(p.updated_at)}</p>
            </div>
            {p.status === "published" && <Link to={`/insights/${p.slug}`} target="_blank" data-testid={`admin-view-post-${p.id}`} className="rounded-full p-2.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900" aria-label="View"><ExternalLink className="h-4 w-4" /></Link>}
            <Link to={`/admin/posts/${p.id}`} data-testid={`admin-edit-post-${p.id}`} className="rounded-full p-2.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900" aria-label="Edit"><Pencil className="h-4 w-4" /></Link>
            <button type="button" onClick={() => setDel(p)} data-testid={`admin-delete-post-${p.id}`} className="rounded-full p-2.5 text-slate-500 hover:bg-red-50 hover:text-red-600" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <TranslationsPanel />
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent data-testid="admin-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this post?</AlertDialogTitle>
            <AlertDialogDescription>“{del?.title}” will be permanently removed from Insights.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="admin-delete-confirm" className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
